import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  org_admin: 'Organization Admin',
  org_member: 'Organization Member',
};

const RESTRICTED_SCREEN_LABELS = {
  '/organization-settings': 'Organization Settings',
  '/permission-audit-dashboard': 'Permission Audit',
  '/user-role-management': 'User Management',
};

const AccessDenied = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, isSuperAdmin, isOrgAdmin } = useAuth();

  const attemptedPath = location?.state?.from || null;
  const screenLabel = attemptedPath ? (RESTRICTED_SCREEN_LABELS?.[attemptedPath] || 'this area') : 'this area';
  const currentRole = userProfile?.system_role;
  const roleLabel = ROLE_LABELS?.[currentRole] || 'your current role';

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    if (isSuperAdmin) {
      navigate('/superadmin-dashboard');
    } else if (isOrgAdmin) {
      navigate('/dashboard');
    } else {
      navigate('/my-dashboard');
    }
  };

  const accessibleScreens = isSuperAdmin
    ? [
        { label: 'Superadmin Dashboard', path: '/superadmin-dashboard', icon: 'LayoutDashboard' },
        { label: 'Organization Management', path: '/organization-management', icon: 'Building2' },
        { label: 'User Role Management', path: '/user-role-management', icon: 'Users' },
      ]
    : isOrgAdmin
    ? [
        { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
        { label: 'Ticket Management', path: '/ticket-management', icon: 'ClipboardList' },
        { label: 'Personnel Management', path: '/personnel-management', icon: 'Users' },
      ]
    : [
        { label: 'My Dashboard', path: '/my-dashboard', icon: 'LayoutDashboard' },
        { label: 'Ticket Management', path: '/ticket-management', icon: 'ClipboardList' },
        { label: 'Support Requests', path: '/support-request-management', icon: 'MessageSquare' },
      ];

  return (
    <>
      <Helmet>
        <title>Access Denied — ServiceJockey</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-16">
        {/* Icon badge */}
        <div className="w-20 h-20 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-6 shadow-sm">
          <Icon name="ShieldOff" size={36} color="#dc2626" />
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Access Denied
        </h1>

        {/* Sub-message */}
        <p className="text-gray-500 text-sm text-center max-w-sm mb-1">
          You don't have permission to access{' '}
          <span className="font-medium text-gray-700">{screenLabel}</span>.
        </p>
        <p className="text-gray-400 text-xs text-center max-w-sm mb-8">
          This area requires <span className="font-medium text-gray-600">Organization Admin</span> or higher privileges.
          You are currently signed in as{' '}
          <span className="font-medium text-gray-600">{roleLabel}</span>
          {userProfile?.role_name ? ` (${userProfile?.role_name})` : ''}.
        </p>

        {/* Required permission callout */}
        <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-8 flex items-start gap-3">
          <Icon name="Info" size={16} color="#d97706" className="mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            To gain access, contact your Organization Admin to update your role or permissions.
          </p>
        </div>

        {/* Accessible screens */}
        <div className="w-full max-w-sm mb-8">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Screens you can access
          </p>
          <div className="flex flex-col gap-2">
            {accessibleScreens?.map((screen) => (
              <button
                key={screen?.path}
                onClick={() => navigate(screen?.path)}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors shrink-0">
                  <Icon name={screen?.icon} size={15} color="#6b7280" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700 transition-colors">
                  {screen?.label}
                </span>
                <Icon name="ChevronRight" size={14} color="#9ca3af" className="ml-auto" />
              </button>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Icon name="ArrowLeft" size={14} color="#6b7280" />
            Go Back
          </button>
          <button
            onClick={handleGoHome}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Icon name="Home" size={14} color="#ffffff" />
            Go to Dashboard
          </button>
        </div>
      </div>
    </>
  );
};

export default AccessDenied;
